import React, { createContext, useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  

  // Fetch user's clinic data
  const fetchClinicData = async (userId) => {
    try {
      const clinicFields = `
        id,
        name,
        city,
        address,
        street_number,
        more_info,
        mobile_phone,
        line_phone,
        slug,
        logo_url,
        primary_color,
        accent_color,
        public_intro_text,
        theme_id,
        user_id,
        owner_first_name,
        owner_last_name,
        vat_number,
        created_at,
        updated_at
      `;

      // First try to find clinic where user is the owner
      const { data, error } = await supabase
        .from('clinics')
        .select(clinicFields)
        .eq('user_id', userId)
        .single();

      if (error && error.code === 'PGRST116') {
        // No clinic found - user might be a vet, check vets table
        const { data: vetData, error: vetError } = await supabase
          .from('vets')
          .select('clinic_id')
          .eq('user_id', userId)
          .single();

        if (vetError) {
          if (vetError.code !== 'PGRST116') {
            if (import.meta.env.DEV) console.error('Error fetching vet data:', vetError);
          }
          return null;
        }

        // Fetch the full clinic data using the vet's clinic_id
        const { data: clinicData, error: clinicError } = await supabase
          .from('clinics')
          .select(clinicFields)
          .eq('id', vetData.clinic_id)
          .single();

        if (clinicError) {
          if (import.meta.env.DEV) console.error('Error fetching clinic by id:', clinicError);
          return null;
        }

        return clinicData;
      }

      if (error) {
        if (import.meta.env.DEV) console.error('Error fetching clinic:', error);
        setError('Failed to fetch clinic data');
        return null;
      }

      return data;
    } catch (err) {
      if (import.meta.env.DEV) console.error('Error fetching clinic data:', err);
      setError('Failed to fetch clinic data');
      return null;
    }
  };

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;
    
    if (import.meta.env.DEV) console.log('AuthContext: Initializing authentication...');

    // Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (isMounted && loading) {
        if (import.meta.env.DEV) console.warn('Auth initialization timeout - forcing loading to false');
        setLoading(false);
      }
    }, 5000); // Reduced to 5 seconds

    // Add a second, more aggressive timeout
    const aggressiveTimeoutId = setTimeout(() => {
      if (isMounted && loading) {
        if (import.meta.env.DEV) console.error('CRITICAL: Auth loading stuck - forcing all states to false');
        setLoading(false);
        setError('Authentication timeout - please refresh the page');
      }
    }, 10000); // 10 seconds
    
    const initializeAuth = async () => {
      try {
        if (import.meta.env.DEV) console.log('AuthContext: Getting session...');
        // Get current session
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          if (import.meta.env.DEV) console.error('Error getting session:', error);
          if (isMounted) {
            setError(error.message);
            setLoading(false);
          }
          return;
        }

        if (import.meta.env.DEV) console.log('AuthContext: Session result:', { hasSession: !!session, userId: session?.user?.id });

        if (session?.user) {
          if (isMounted) {
            setUser(session.user);
            Sentry.setUser({ id: session.user.id, email: session.user.email });
            if (import.meta.env.DEV) console.log('AuthContext: User authenticated, loading complete');
            setLoading(false);

            // Fetch clinic data in background (don't block UI)
            fetchClinicData(session.user.id).then(clinicData => {
              if (isMounted) {
                setClinic(clinicData);
                if (import.meta.env.DEV) console.log('AuthContext: Clinic data fetched in background:', clinicData);
              }
            }).catch(err => {
              if (import.meta.env.DEV) console.warn('AuthContext: Clinic data fetch failed (non-blocking):', err);
            });
          }
        } else {
          if (isMounted) {
            setLoading(false);
            if (import.meta.env.DEV) console.log('AuthContext: No session, loading complete');
          }
        }
      } catch (err) {
        if (import.meta.env.DEV) console.error('Error initializing auth:', err);
        if (isMounted) {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (import.meta.env.DEV) console.log('Auth state changed:', event, session?.user?.id);

        if (!isMounted) return;

        if (session?.user) {
          setUser(session.user);
          Sentry.setUser({ id: session.user.id, email: session.user.email });
          setError(null);
          setLoading(false); // Set loading to false immediately

          // Fetch clinic data in background
          fetchClinicData(session.user.id).then(clinicData => {
            if (isMounted) {
              setClinic(clinicData);
            }
          }).catch(err => {
            if (import.meta.env.DEV) console.warn('AuthContext: Clinic data fetch failed (non-blocking):', err);
          });
        } else {
          setUser(null);
          setClinic(null);
          setError(null);
          setLoading(false);
          Sentry.setUser(null); // Clear user context on sign-out
        }
      }
    );

    return () => {
      if (import.meta.env.DEV) console.log('AuthContext: Cleaning up...');
      isMounted = false;
      clearTimeout(timeoutId);
      clearTimeout(aggressiveTimeoutId);
      subscription.unsubscribe();
    };
  }, []);

  // Sign out function
  const signOut = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        throw error;
      }
      
      setUser(null);
      setClinic(null);
      setError(null);
    } catch (err) {
      if (import.meta.env.DEV) console.error('Error signing out:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Refresh clinic data
  const refreshClinicData = async () => {
    if (user) {
      const clinicData = await fetchClinicData(user.id);
      setClinic(clinicData);
      return clinicData;
    }
    return null;
  };

  const value = {
    user,
    clinic,
    loading,
    error,
    signOut,
    refreshClinicData,
    isAuthenticated: !!user,
    hasClinic: !!clinic
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 